import { Component, CUSTOM_ELEMENTS_SCHEMA, effect, inject, input, NgZone, OnDestroy, OnInit, output, signal } from '@angular/core';

import { defer, first } from 'rxjs';

import { CaptchaCapWidgetService } from '@bn/auth/services/captcha-cap-widget.service';
import { ENV_CONFIG } from '@bn/primitives';
import { AlertComponent } from '@bn/web-ui/alert/alert.component';
import { LoadingComponent } from '@bn/web-ui/loading/loading.component';

@Component({
  selector: 'bn-auth-captcha-widget',
  templateUrl: './captcha.component.html',
  styleUrl: './captcha.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [AlertComponent, LoadingComponent],
})
export class CaptchaComponent implements OnDestroy, OnInit {
  #captchaService = inject(CaptchaCapWidgetService);
  #capWidgetScript!: HTMLScriptElement;
  challengeUrl = inject(ENV_CONFIG).captchaWidgetChallengeUrl;
  errorOnlyCaptcha = input(true);
  useDefaultWidgetCaptcha = input(false);
  isError = signal('');
  isRobot = signal(false);
  progressPercent = signal(0);
  #ngZone = inject(NgZone);

  readonly hasPassed = output<boolean>();
  readonly powTokenEvent = output<string>();

  constructor() {
    effect(() => this.hasPassed.emit(!this.isRobot()));

    this.#ngZone.runOutsideAngular(() => {
      this.getTokenValueFromDom();
    });
  }

  ngOnInit() {
    this.initializeWidget();
  }

  ngOnDestroy() {
    if (this.#capWidgetScript) {
      document.body.removeChild(this.#capWidgetScript);
    }
  }

  private initializeWidget() {
    this.#captchaService
      .loadCaptcha()
      .then(() => {
        if (this.useDefaultWidgetCaptcha()) {
          // Default cap widget design
          const widget = document.querySelector('#captcha-widget');
          this.initEventListeners(widget);
        } else {
          // Custom hidden captcha
          const cap = this.#captchaService.hiddenCapInstance;
          this.initEventListeners(cap);

          // Manually run hidden captcha check
          defer(() => cap.solve())
            .pipe(first())
            .subscribe();
        }
      })
      .catch((error) => console.error(error));
  }

  private initEventListeners(widget: Element | null): void {
    if (widget) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      widget.addEventListener('solve', (e: any) => {
        const token = e.detail.token;
        this.isError.set('');
        this.isRobot.set(!token);
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      widget.addEventListener('error', (e: any) => {
        this.isError.set(e.detail.message);
        this.isRobot.set(this.isError() === 'Invalid solution');
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      widget.addEventListener('progress', (e: any) => {
        this.progressPercent.set(+e.detail.progress);
      });
    }
  }

  getTokenValueFromDom() {
    // MutationObserver listens for DOM changes.
    const observer = new MutationObserver((mutations, obs) => {
      const token = document.querySelector<HTMLInputElement>('input[name="cap-token"]')?.value ?? '';
      if (token !== '') {
        this.powTokenEvent.emit(token);

        // Stop observing once found
        obs.disconnect();
      }

      // clear timeout after some time
      setTimeout(() => {
        obs.disconnect();
      }, 10_000);
    });

    observer.observe(document.body, {
      childList: false,
      subtree: true,
      attributes: true,
    });
  }
}
